import {useCallback, useEffect} from 'react';
import {UserProfile} from 'APIs/letrusAPI/user/types';
import {updateProfileService} from 'store/services/profileServices';
import {updateUserData} from 'utils/functions/getUserData/getUserData';

export interface ClosableDialog {
  addEventListener(type: 'close', listener: () => void): void;
  removeEventListener(type: 'close', listener: () => void): void;
}

// Persists ai_notice_seen_at (backend field, account-wide) by listening to
// the dialog's native `close` event — catches every way the modal can
// close ("Entendi" button, the built-in X, or ESC), not just an explicit
// button click. Same convention as useCloseTransferNotice: dialog control
// stays delegated to the parent via letrus-ui's useModals.
export function useCloseAiNotice(
  userProfile: UserProfile | undefined,
  modal: ClosableDialog | null | undefined,
) {
  const closeAiNotice = useCallback(async () => {
    if (!userProfile?.id || userProfile.ai_notice_seen_at) {
      return;
    }

    const seenAt = new Date().toISOString();

    try {
      await updateProfileService({
        id: userProfile.id,
        ai_notice_seen_at: seenAt,
      });

      // Keeps the cached session in sync so a page reload doesn't show the
      // notice again before the next /api/v1/user_profile/ refetch.
      updateUserData('ai_notice_seen_at', seenAt);
    } catch (error) {
      console.error(error);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!modal) {
      return undefined;
    }

    modal.addEventListener('close', closeAiNotice);

    return () => {
      modal.removeEventListener('close', closeAiNotice);
    };
  }, [modal, closeAiNotice]);
}

export default useCloseAiNotice;
